import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Stack, Container } from '@fiverr-private/layout_components';
import { Text } from '@fiverr-private/typography';
import { Button } from '@fiverr-private/button';
import { ErrorCircleIcon, PlusIcon } from '@fiverr-private/visuals';
import { EditComponentProps } from '../..';
import { EditActions } from '../../EditActions';
import { ScreeningQuestion, ScreeningQuestionsContent } from '../utils/types';
import { MAX_QUESTIONS, SUGGESTED_QUESTION_TRANSLATION_KEYS } from '../utils/constants';
import { AppI18n, appTranslate } from '../../../../i18n';
import { SuggestedQuestion } from './SuggestedQuestion';
import { AddedQuestionInput } from './AddedQuestionInput';

interface AddSuggestedQuestionProps {
  text: string;
  suggestedQuestionId: string;
}

type ScreeningSuggestedQuestion = ScreeningQuestion & Required<Pick<ScreeningQuestion, 'suggestedQuestionId'>>;

export type EditScreeningQuestionsProps = EditComponentProps<Partial<ScreeningQuestionsContent>>;

const createEmptyQuestion = (): ScreeningQuestion => ({
  id: uuidv4(),
  text: '',
});

const createClientDefaultState = (): ScreeningQuestion[] => [createEmptyQuestion()];

export const EditScreeningQuestions: React.FC<EditScreeningQuestionsProps> = ({
  section,
  onChange,
  onEditEnd,
  onCancel,
  shouldRemoveSection,
}) => {
  const [questions, _setQuestions] = useState(section.content?.questions ?? []);
  const hasOnlyEmptyQuestion = questions.length === 1 && questions[0].text === '';
  const allQuestionsAreEmpty = questions.every((question) => question.text === '');
  const [isDirty, setIsDirty] = useState(Boolean(questions.length));

  const updateQuestions = useCallback((newQuestions: ScreeningQuestion[], shouldSetDirty = true) => {
    const questionsToApply = newQuestions.length ? newQuestions : createClientDefaultState();

    if (questionsToApply.length > MAX_QUESTIONS) {
      return;
    }

    if (shouldSetDirty) {
      setIsDirty(true);
    }
    _setQuestions(questionsToApply);
  }, []);

  useEffect(() => {
    if (!questions.length) {
      updateQuestions(createClientDefaultState(), false);
    }
  }, [questions.length, updateQuestions]);

  const availableSuggestedQuestionIds = useMemo(() => {
    const usedIds = new Set(
      questions
        .filter((question): question is ScreeningSuggestedQuestion => Boolean(question.suggestedQuestionId))
        .map(({ suggestedQuestionId }) => suggestedQuestionId)
    );

    return SUGGESTED_QUESTION_TRANSLATION_KEYS.filter((id) => !usedIds.has(id));
  }, [questions]);

  const isMaxQuestionsReached = questions.length >= MAX_QUESTIONS;
  const hasBlankQuestion = questions.some((question) => !question.text.trim());

  const handleAddNewEmptyQuestion = () => {
    updateQuestions([...questions, createEmptyQuestion()]);
  };

  const handleAddSuggestedQuestion = ({ text, suggestedQuestionId }: AddSuggestedQuestionProps) => {
    const emptySlotIndex = questions.findIndex((question) => !question.text.trim());

    if (emptySlotIndex !== -1) {
      updateQuestions(
        questions.map((question, index) =>
          index === emptySlotIndex ? { ...question, text, suggestedQuestionId } : question
        )
      );
      return;
    }

    updateQuestions([...questions, { id: uuidv4(), text, suggestedQuestionId }]);
  };

  const handleSave = () => {
    const questionsToSave = questions.filter((question) => Boolean(question.text.trim()));

    onChange({ questions: questionsToSave });
    onEditEnd?.();
  };

  const handleRemoveQuestion = (id: string) => {
    if (hasOnlyEmptyQuestion) {
      return;
    }

    updateQuestions(questions.filter((question) => question.id !== id));
  };

  const handleUpdateQuestion = (id: string, newText: string) => {
    updateQuestions(questions.map((question) => (question.id === id ? { ...question, text: newText } : question)));
  };

  return (
    <Container backgroundColor="grey_100" borderRadius="xl">
      <Stack direction="column" gap="6">
        <Stack direction="column" gap="2">
          <Text size="b_sm" color="bodySecondary">
            <AppI18n k="sections.screening_questions.description" />
          </Text>
        </Stack>

        <Container minHeight={questions.length > 0 ? undefined : '68px'}>
          {questions.length > 0 && (
            <Stack direction="column" gap="4">
              {questions.map((question) => (
                <AddedQuestionInput
                  key={question.id}
                  value={question.text}
                  onChange={(newText) => handleUpdateQuestion(question.id, newText)}
                  onDelete={() => handleRemoveQuestion(question.id)}
                  isCounterVisible={hasOnlyEmptyQuestion}
                  isRemoveDisabled={hasOnlyEmptyQuestion}
                />
              ))}

              {isMaxQuestionsReached && (
                <Stack marginBottom="2" direction="row" gap="2" alignItems="center">
                  <ErrorCircleIcon size="sm" />
                  <Text size="b_sm" color="bodyPrimary">
                    <AppI18n k="sections.screening_questions.max_questions_reached" />
                  </Text>
                </Stack>
              )}
            </Stack>
          )}
        </Container>

        {!isMaxQuestionsReached && isDirty && (
          <Container>
            <Button
              size="sm"
              intent="secondary"
              variant="outline"
              onClick={handleAddNewEmptyQuestion}
              disabled={hasBlankQuestion}
            >
              <PlusIcon />
              <AppI18n k="sections.screening_questions.add_another_question_button" />
            </Button>
          </Container>
        )}

        {Boolean(availableSuggestedQuestionIds.length) && (
          <Stack direction="column" gap="4">
            <Text size="b_sm" fontWeight="semibold">
              <AppI18n k="sections.screening_questions.suggested_questions_title" />
            </Text>
            <Stack direction="column" gap="4">
              {availableSuggestedQuestionIds.map((id) => (
                <SuggestedQuestion
                  key={id}
                  question={appTranslate<string>(id)}
                  onSelect={(text) => handleAddSuggestedQuestion({ text, suggestedQuestionId: id })}
                  disabled={isMaxQuestionsReached}
                />
              ))}
            </Stack>
          </Stack>
        )}

        <EditActions
          onCancel={onCancel}
          onEditEnd={handleSave}
          shouldRemoveSection={shouldRemoveSection}
          isSaveDisabled={shouldRemoveSection && allQuestionsAreEmpty}
        />
      </Stack>
    </Container>
  );
};
